/**
 * Environment Configuration
 * LegalSyncUp - Swiss Legal Compliance Platform
 *
 * Database: Neon PostgreSQL (syncup-brand-legal)
 * App Data: legalsyncup schema
 *
 * DUAL AUTH PROVIDER ARCHITECTURE:
 * ================================
 * This application supports TWO production-ready authentication providers:
 *
 * 1. NEON AUTH (powered by Better Auth) - Default
 *    - Direct authentication via @neondatabase/neon-js
 *    - Native Cloudflare Workers support
 *    - Shared neon_auth schema with BrandSyncUp
 *    - Best for: API-first auth, direct sign-in/sign-up
 *
 * 2. FIREBASE AUTH (with Identity Platform) - Enterprise
 *    - Google Cloud Identity Platform integration
 *    - Multi-tenant identity management
 *    - Enterprise SSO (SAML/OIDC)
 *    - Best for: Enterprise customers, Google Workspace, multi-tenant scenarios
 *
 * Provider selection via VITE_USE_FIREBASE_AUTH environment variable.
 * Both providers are PRODUCTION-READY.
 */

// Neon Database Configuration
const NEON_DATABASE_URL = import.meta.env.VITE_NEON_DATABASE_URL || import.meta.env.VITE_DATABASE_URL || '';
const NEON_PROJECT_ID = 'polished-truth-90679079';
const NEON_BRANCH_PRODUCTION = 'br-cool-bird-agmnaox6';
const NEON_BRANCH_DEVELOPMENT = 'br-polished-sun-ag8a15yi';

// Neon Auth Configuration (powered by Better Auth)
// Note: This must match the database endpoint (ep-odd-dawn-aggx168n)
const NEON_AUTH_URL = import.meta.env.VITE_NEON_AUTH_URL || 'https://ep-odd-dawn-aggx168n.neonauth.c-2.eu-central-1.aws.neon.tech/neondb/auth';
const NEON_AUTH_JWKS_URL = import.meta.env.VITE_NEON_AUTH_JWKS_URL || 'https://ep-odd-dawn-aggx168n.neonauth.c-2.eu-central-1.aws.neon.tech/neondb/auth/.well-known/jwks.json';

// API Configuration
const API_URL = import.meta.env.VITE_API_URL || '';

export const appConfig = {
  // Neon Database
  NEON_DATABASE_URL,
  NEON_PROJECT_ID,
  NEON_BRANCH: import.meta.env.PROD ? NEON_BRANCH_PRODUCTION : NEON_BRANCH_DEVELOPMENT,

  // Neon Auth (powered by Better Auth)
  NEON_AUTH_URL,
  NEON_AUTH_JWKS_URL,

  // API
  API_URL,

  // Environment
  ENVIRONMENT: import.meta.env.MODE || 'development',
  DEBUG: import.meta.env.DEV,
  IS_DEVELOPMENT: import.meta.env.DEV,
  IS_PRODUCTION: import.meta.env.PROD,

  // App Info
  APP_VERSION: '1.0.0',
  APP_DOMAIN: 'legalsyncup.com',
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '',

  // Database Configuration
  DATABASE: {
    SCHEMA: 'legalsyncup',
    AUTH_SCHEMA: 'neon_auth',
  },

  FEATURES: {
    AUDIT_LOGGING: true,
    CSP_STRICT_MODE: true,
    RATE_LIMITING: true,
    REAL_TIME_UPDATES: true,
    ADVANCED_ANALYTICS: true,
    ML_FEATURES: true,
    DEBUG_MODE: import.meta.env.DEV,
    /**
     * DUAL AUTH PROVIDER FEATURE FLAGS
     * =================================
     * Both auth providers are PRODUCTION-READY for their respective use cases.
     *
     * VITE_USE_FIREBASE_AUTH:
     * - Default: false (uses Better Auth for direct authentication)
     * - Set to 'true' to enable Firebase Auth for enterprise identity scenarios
     *
     * VITE_USE_NEON_AUTH:
     * - Default: false (uses Better Auth with Worker API)
     * - Set to 'true' to use Neon's hosted auth (may have cross-domain cookie issues)
     *
     * USE BETTER AUTH (default) when:
     * - Direct sign-in/sign-up flows
     * - API-first authentication with Worker API
     * - Sessions stored in neon_auth schema, cookies on app domain
     *
     * USE FIREBASE AUTH when:
     * - Multi-tenant identity management
     * - Enterprise SSO (SAML/OIDC via Google Identity Platform)
     * - Google Workspace integration
     * - Organizations requiring Google Cloud identity services
     */
    USE_NEON_AUTH: import.meta.env.VITE_USE_NEON_AUTH === 'true', // Disabled by default (cross-domain cookie issues)
    USE_BETTER_AUTH: import.meta.env.VITE_USE_FIREBASE_AUTH !== 'true' && import.meta.env.VITE_USE_NEON_AUTH !== 'true', // Default: Worker API auth
    USE_FIREBASE_AUTH: import.meta.env.VITE_USE_FIREBASE_AUTH === 'true', // Enterprise: Firebase for multi-tenant/SSO
  },

  SECURITY: {
    RATE_LIMIT_ENABLED: true,
    CSP_ENABLED: true,
    HSTS_ENABLED: true,
    SECURE_HEADERS_ENABLED: true,
    SESSION_TIMEOUT: 3600000,
    MAX_LOGIN_ATTEMPTS: 5
  }
};

export const envLog = (message: string, ...args: unknown[]): void => {
  if (appConfig.DEBUG) {
    console.log(`[${new Date().toISOString()}] ${message}`, ...args);
  }
};

export const envError = (message: string, error?: unknown): void => {
  console.error(`[${new Date().toISOString()}] ERROR: ${message}`, error);
};

export const envWarn = (message: string, ...args: unknown[]): void => {
  console.warn(`[${new Date().toISOString()}] WARNING: ${message}`, ...args);
};

export const isDevelopment = appConfig.IS_DEVELOPMENT;
export const isProduction = appConfig.IS_PRODUCTION;

export const validateConfig = (): boolean => {
  return !!appConfig.NEON_DATABASE_URL;
};

export const getApiUrl = (): string => {
  return appConfig.API_BASE_URL || appConfig.API_URL;
};

export const getDatabaseUrl = (): string => {
  return appConfig.NEON_DATABASE_URL;
};

export const isNeonEnabled = (): boolean => {
  return !!appConfig.NEON_DATABASE_URL;
};

export const isBetterAuthEnabled = (): boolean => {
  return appConfig.FEATURES.USE_BETTER_AUTH;
};

export const isNeonAuthEnabled = (): boolean => {
  return appConfig.FEATURES.USE_NEON_AUTH;
};

export const isFirebaseAuthEnabled = (): boolean => {
  return appConfig.FEATURES.USE_FIREBASE_AUTH;
};

/**
 * Get the active authentication provider
 *
 * DUAL AUTH PROVIDER SELECTION:
 * =============================
 * Both providers are production-ready for their intended use cases:
 *
 * - FIREBASE: Enterprise identity (SSO, multi-tenant, Google Identity Platform)
 * - BETTER: Default Worker API auth (sessions on app domain, neon_auth schema)
 * - NEON: Neon hosted auth (opt-in, may have cross-domain cookie issues)
 *
 * @returns 'firebase' | 'neon' | 'better'
 */
export const getAuthProvider = (): 'firebase' | 'neon' | 'better' => {
  // FIREBASE AUTH: Enterprise identity management
  // Enable via VITE_USE_FIREBASE_AUTH=true for multi-tenant/SSO scenarios
  if (isFirebaseAuthEnabled()) return 'firebase';

  // NEON AUTH: Opt-in Neon hosted auth
  // Enable via VITE_USE_NEON_AUTH=true (note: may have cross-domain cookie issues)
  if (isNeonAuthEnabled()) return 'neon';

  // BETTER AUTH: Default Worker API authentication
  // Sessions stored in neon_auth schema, cookies set on app domain
  return 'better';
};
